<script setup lang="ts">
// Vue & Plugins
import { computed, inject, ref, Ref } from 'vue'
import { currentSiteKey } from '../../../common/injection'
import { Site } from '../../../sites/models/site'
import { useEmployeeSelectDialog } from '../../composables/useEmployeeSelectDialog'
import { Employee } from '../../models/employee'
import { eligibleQueryOptions } from '../../composables/useEmployeeQuery'
import { Pagination } from '../../../common/models/pagination'
import { useQuery } from '@tanstack/vue-query'
import EmployeeSelectCard from '../cards/EmployeeSelectCard.vue'
import { DateTime } from 'luxon'
import EmployeeSelectedCard from '../cards/EmployeeSelectedCard.vue'
import { refDebounced } from '@vueuse/core'
import { Group } from '../../../groups/models/group'
import GroupSelect from '../../../groups/components/input/GroupSelect.vue'
import InfiniteScrollWrapper from '../../../common/components/InfiniteScrollWrapper.vue'

const site = inject(currentSiteKey) as Ref<Site>

const {
  isShown,
  employees,
  areaId,
  hide,
  confirm,
  cancel,
  addEmployees,
  removeEmployee,
  removeAll,
} = useEmployeeSelectDialog()

const handleSelect = (employees: Employee[]) => {
  confirm(employees)
}

const handleCancel = () => {
  cancel()
}

// const handleClose = () => {
//     hide()
// }

const enabled = computed(() => isShown.value)

const nameInput = ref('')
const name = refDebounced(nameInput, 500)

const group: Ref<Group | null> = ref(null)

const { data, error, isFetching } = useQuery<
  { employees: Employee[]; pagination: Pagination },
  Error
>({
  ...eligibleQueryOptions(() => site.value.id, {
    areaId: areaId,
    startDate: DateTime.now(),
    endDate: DateTime.now().plus({ days: 7 }),
    name: name,
    group: group,
  }),
  enabled: enabled,
})

const options = computed<Employee[]>(() => data.value?.employees ?? [])
</script>

<template>
  <Dialog
    :visible="isShown"
    header="Select Employees"
    dismissable-mask
    modal
    class="h-screen w-3/4 max-w-7xl"
    :content-class="'h-full'"
    @update:visible="hide"
  >
    <div class="flex h-full border-y">
      <aside class="flex w-64 flex-col border-r">
        <div role="list" class="grid grid-cols-1 gap-2 overflow-y-auto pr-3">
          <EmployeeSelectedCard
            v-for="employee in employees"
            :key="employee.id"
            :employee="employee"
            @remove-employee="(employee) => removeEmployee(employee)"
          />
        </div>
      </aside>

      <div class="flex grow flex-col pl-2 pt-2">
        <Toolbar>
          <template #start>
            <div class="flex gap-x-2">
              <InputText v-model="nameInput" label="Name" placeholder="Name" />

              <GroupSelect
                v-model:selected="group"
                :site-id="site.id"
                show-clear
              />
            </div>
          </template>

          <template #end>
            <Button
              label="Clear"
              :loading="isFetching"
              :severity="'secondary'"
              @click="removeAll"
            />
          </template>
        </Toolbar>

        <InfiniteScrollWrapper
          height-class="h-full"
          :is-empty="options.length === 0"
          :is-fetching="isFetching"
          :error="error?.message"
          :has-next-page="false"
        >
          <ul role="list" class="grid grid-cols-6 gap-2 pr-3">
            <EmployeeSelectCard
              v-for="employee in options"
              :key="employee.id"
              :employee="employee"
              :selected="employees.some((item) => item.id === employee.id)"
              @add-employee="(employee) => addEmployees([employee])"
              @remove-employee="(employee) => removeEmployee(employee)"
            />
          </ul>
        </InfiniteScrollWrapper>
      </div>
    </div>

    <template #footer>
      <Button
        label="Confirm"
        :loading="isFetching"
        severity="primary"
        @click="handleSelect(employees)"
      />

      <Button
        label="Cancel"
        :loading="isFetching"
        severity="secondary"
        @click="handleCancel"
      />
    </template>
  </Dialog>
</template>
